import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cardinal } from '../types';

interface ElectorsListProps {
  cardinaux: Cardinal[];
}

const ElectorsList: React.FC<ElectorsListProps> = ({ cardinaux }) => {
  // Fonction pour créer un slug à partir du nom du cardinal
  const createSlug = (cardinal: Cardinal): string => {
    // Use lastName and firstName if available, otherwise fallback to name
    const nameToUse = cardinal.lastName && cardinal.firstName
      ? `${cardinal.firstName}-${cardinal.lastName}`
      : cardinal.name;
    return nameToUse.toLowerCase().replace(/\s+/g, '-');
  };
  
  // Fonction pour déterminer quel pape a créé le cardinal
  const getCreatorPope = (creationDate?: string, createdByFrancis?: boolean): string => {
    if (createdByFrancis) return "Créé Cardinal par le Pape François";
    
    if (!creationDate) return "";
    
    // Convertir la date au format JJ/MM/AAAA en objet Date
    const parts = creationDate.split('/');
    if (parts.length !== 3) return "";
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Les mois commencent à 0 en JavaScript
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    // Dates des pontificats
    const benedictXVIStart = new Date(2005, 3, 19); // 19 avril 2005
    const benedictXVIEnd = new Date(2013, 1, 28);   // 28 février 2013
    
    if (date >= benedictXVIStart && date <= benedictXVIEnd) {
      return "Créé Cardinal par le Pape Benoît XVI";
    } else if (date < benedictXVIStart) {
      return "Créé Cardinal par le Pape Jean-Paul II";
    } else {
      return "Créé Cardinal par le Pape François";
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'country' | 'age' | 'creationDate'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filtrer les cardinaux en fonction du terme de recherche
  const filteredCardinaux = cardinaux.filter(cardinal => 
    cardinal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cardinal.lastName && cardinal.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (cardinal.firstName && cardinal.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (cardinal.country && cardinal.country.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (cardinal.function && cardinal.function.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Trier les cardinaux
  const sortedCardinaux = [...filteredCardinaux].sort((a, b) => {
    if (sortBy === 'name') {
      // Use lastName for sorting if available, otherwise fallback to name
      const aName = a.lastName || a.name;
      const bName = b.lastName || b.name;
      return sortDirection === 'asc' 
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    } else if (sortBy === 'country') {
      const countryA = a.country || '';
      const countryB = b.country || '';
      return sortDirection === 'asc'
        ? countryA.localeCompare(countryB)
        : countryB.localeCompare(countryA);
    } else if (sortBy === 'age') {
      const ageA = a.age ? Number(a.age) : 0;
      const ageB = b.age ? Number(b.age) : 0;
      return sortDirection === 'asc' ? ageA - ageB : ageB - ageA;
    } else if (sortBy === 'creationDate') {
      const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
      const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  // Fonction pour changer le tri
  const handleSort = (column: 'name' | 'country' | 'age' | 'creationDate') => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Icône de tri
  const SortIcon = ({ column }: { column: 'name' | 'country' | 'age' | 'creationDate' }) => {
    if (sortBy !== column) return null;
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl p-3 sm:p-6 bg-opacity-80 backdrop-blur-sm overflow-hidden">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
        Liste des cardinaux électeurs
      </h2>
      
      <div className="mb-4 sm:mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher par nom, pays ou fonction..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto -mx-3 sm:-mx-6">
        <div className="inline-block min-w-full align-middle px-3 sm:px-6">
          <table className="min-w-full divide-y divide-gray-700 text-sm sm:text-base">
            <thead className="bg-gray-700">
              <tr>
                <th 
                  scope="col" 
                  className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  Nom <SortIcon column="name" />
                </th>
                <th 
                  scope="col" 
                  className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('country')}
                >
                  Pays <SortIcon column="country" />
                </th>
                <th 
                  scope="col" 
                  className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('age')}
                >
                  Âge <SortIcon column="age" />
                </th>
                <th 
                  scope="col" 
                  className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('creationDate')}
                >
                  Création <SortIcon column="creationDate" />
                </th>
                <th scope="col" className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Fonction
                </th>
                <th scope="col" className="relative px-2 sm:px-6 py-3">
                  <span className="sr-only">Voir</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {sortedCardinaux.map((cardinal, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-gray-700 transition-colors cursor-pointer" 
                  onClick={() => window.location.href = `/cardinal/${createSlug(cardinal)}`}
                >
                  <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden border border-yellow-500">
                        {cardinal.imageUrl ? (
                          <img 
                            src={cardinal.imageUrl} 
                            alt={cardinal.name} 
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/No-image-available.png/200px-No-image-available.png";
                            }}
                          />
                        ) : (
                          <div className="h-full w-full bg-gray-600 flex items-center justify-center text-white">
                            {cardinal.lastName 
                              ? cardinal.lastName.substring(0, 2).toUpperCase() 
                              : cardinal.name.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">
                          {cardinal.url ? (
                            <a 
                              href={cardinal.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-white hover:text-yellow-400 hover:underline"
                            >
                              {cardinal.lastName && cardinal.firstName 
                              ? `${cardinal.firstName} ${cardinal.lastName}` 
                              : cardinal.name}
                            </a>
                          ) : (
                            cardinal.lastName && cardinal.firstName 
                              ? `${cardinal.firstName} ${cardinal.lastName}` 
                              : cardinal.name
                          )}
                        </div>
                        {cardinal.creationDate && (
                          <div className="text-xs text-yellow-500">
                            {getCreatorPope(cardinal.creationDate, cardinal.createdByFrancis)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {cardinal.flagUrl && (
                        <img 
                          src={cardinal.flagUrl} 
                          alt={cardinal.country || ''} 
                          className="h-4 w-6 mr-2 object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="text-sm text-gray-300">{cardinal.country || '-'}</span>
                    </div>
                  </td>
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {cardinal.age ? `${cardinal.age} ans` : '-'}
                  </td>
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {cardinal.creationDate || '-'}
                  </td>
                  <td className="px-2 sm:px-6 py-4 text-sm text-gray-300">
                    <div className="max-w-xs truncate">
                      {cardinal.function || '-'}
                    </div>
                  </td>
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {cardinal.url && (
                      <div className="flex space-x-2">
                        <Link 
                          to={`/cardinal/${createSlug(cardinal)}`} 
                          className="text-yellow-500 hover:text-yellow-400"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Voir fiche
                        </Link>
                        {cardinal.url && (
                          <>
                            <span className="text-gray-500">|</span>
                            <a 
                              href={cardinal.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-yellow-500 hover:text-yellow-400"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Wikipedia
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ElectorsList;
